"use client";
import React, { useState } from "react";
import { SiteLogo } from "@/components/svg";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { useUserProfileStore } from "@/app/(tblondi)/hooks/useUserProfileStore";
import { useUser } from "@clerk/nextjs";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-breakpoint";
import HorizontalHeaderMobile from "./horizontal-header-mobile";

// Role-based route configuration
const routeConfig = {
  creator: {
    profile: "/v1/profile/edit",
    campaigns: "/v1/search/advanced/campaigns",
    creators: "/v1/search/advanced/creators",
    campaignTasks: "/v1/campaigns/tasks",
  },
  brand: {
    profile: "/v1/profile/edit",
    campaigns: "/v1/search/campaign",
    creators: "/v1/search/advanced/creators",
    campaignTasks: "/v1/campaigns/tasks",
  },
  // Default fallback configuration
  default: {
    profile: "/v1/profile/edit",
    campaigns: "/v1/search/advanced/campaigns",
    creators: "/v1/search/advanced/creators",
    campaignTasks: "/v1/campaigns/tasks",
  },
};

const menus = [
  {
    title: "Profile",
    routeKey: "profile",
  },
  {
    title: "Creators",
    routeKey: "creators",
  },
  {
    title: "Campaigns",
    routeKey: "campaigns",
  },
  {
    title: "Campaign Tasks",
    routeKey: "campaignTasks",
  },
];

const horizontalHeader = ({
  handleOpenSearch,
}: {
  handleOpenSearch: () => void;
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: profileStore } = useUserProfileStore();
  const userType = profileStore?.currentProfile?.type;
  const { isSignedIn } = useUser();
  const isMobile = useIsMobile();

  // Get the appropriate route configuration based on user type
  const config =
    routeConfig[userType as keyof typeof routeConfig] || routeConfig.default;

  // Map menus with their routes and hide Profile for signed-out users
  const filteredMenus = menus
    .filter((menu) => (menu.routeKey === "profile" ? !!isSignedIn : true))
    .map((menu) => ({
      ...menu,
      href:
        !isSignedIn && menu.routeKey === "creators"
          ? "/v1/demo/creators"
          : !isSignedIn && menu.routeKey === "campaigns"
          ? "/v1/demo/campaigns"
          : !isSignedIn && menu.routeKey === "campaignTasks"
          ? "/v1/demo/campaigns/tasks"
          : (config[menu.routeKey as keyof typeof config] as string),
    }));

  // Get action button configuration based on user type
  const getActionButtonConfig = () => {
    if (!isSignedIn) {
      return {
        href: "/v1/demo/campaigns/create",
        label: "Create Campaign",
      };
    }
    if (userType === "brand") {
      return {
        href: "/v1/campaigns/create",
        label: "Create Campaign",
      };
    }
    // if (userType === "creator") {
    //   return {
    //     href: "/v1/search/advanced/campaigns",
    //     label: "Find Campaign",
    //   };
    // }
    return null;
  };

  const actionButtonConfig = getActionButtonConfig();

  // Desktop action button component
  const ActionButton = ({ className }: { className?: string }) => {
    if (!actionButtonConfig) return null;
    
    return (
      <Link href={actionButtonConfig.href}>
        <Button className={cn("bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold", className)}>
          {actionButtonConfig.label}
        </Button>
      </Link>
    );
  };

  // Listen for global event to open mobile menu from right-side burger
  React.useEffect(() => {
    const handler = () => setMobileMenuOpen(true);
    window.addEventListener("open-horizontal-mobile-menu", handler);
    return () => window.removeEventListener("open-horizontal-mobile-menu", handler);
  }, []);

  return (
    <>
      {/* Desktop/Tablet Layout */}
      {!isMobile && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <Link href="/v1/dashboard" className="text-primary flex items-center">
              <div className="w-32 h-8">
                <SiteLogo className="w-full h-full" />
              </div>
            </Link>
            <nav className="hidden lg:flex items-center space-x-4 justify-between px-4" data-guide-step="header-tabs">
              {filteredMenus.map((item) => (
                <Link key={item.title} href={item.href} className="text-primary">
                  <span className="text-lg">{item.title}</span>
                </Link>
              ))}
            </nav>
            <div className="hidden lg:block">
              <ActionButton />
            </div>
          </div>
        </div>
      )}

      {/* Mobile Layout */}
      {isMobile && (
        <>
          <div className="flex items-center justify-between">
            <Link href="/v1/dashboard" className="text-primary flex items-center">
              <div className="w-32 h-8">
                <SiteLogo className="w-full h-full" />
              </div>
            </Link>
          </div>
          <HorizontalHeaderMobile
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            filteredMenus={filteredMenus}
            actionButtonConfig={actionButtonConfig}
          />
        </>
      )}
    </>
  );
};

export default horizontalHeader;
